//-- Copyright (c) 2021 TMT Insights Group, LLC
import { ColumnConfig } from '@/__configs/config_types';
import { convertStylesToStyled, getLineageMetaTitle } from '@/_utils/utils';
import TableCollapse from '@/components/_common/Table/TableCollapse';
import { applyFilter, clearFilters, setCurrentTable } from '@/redux/slices/dataTableSlice';
import { Order, OrderJob } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { Button, Grid, Skeleton, Typography } from '@mui/material';
import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { StatusColors } from '../../constants/constants';
import { selectJobStages, selectJobStatuses } from '../../redux/selectors/constantsSelectors';
import JobTypeLabel from '../Job/JobTypeLabel';
import DateTimeDisplay from '../_common/DateTimeDisplay';
import { JobRequirements } from '../_common/JobRequirements';
import PolarisIcon from '../_common/PolarisIcon';
import TableStatus from '../_common/Table/TableStatus';
import JobsEmbargoDate from '@/components/Jobs/JobsEmbargoDate';
import axios from '@/_utils/axios';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { findandFormatUTCDateInString } from '@/_utils/date';

type OrdersRowExpandProps = {
  isCollapsed: boolean;
  extraColumns?: any[];
  item: Order;
};

const OrdersRowExpand = ({ isCollapsed, extraColumns = [], item }: OrdersRowExpandProps): JSX.Element => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const stages = useAppSelector(selectJobStages);
  const statuses = useAppSelector(selectJobStatuses);
  //KYLE TODO: Once all clients have been updated to remove order.jobs, this can be changed to default undefined
  const [jobs, setJobs] = useState<OrderJob[] | undefined>(item.jobs);
  const sortedJobs = useMemo(() => (jobs ? Array.prototype.slice.call(jobs).sort((a, b) => a.id - b.id) : undefined), [jobs]);
  const firstTenJobs = useMemo(() => sortedJobs?.slice(0, 10) || [], [sortedJobs]);
  const hasMoreJobs = item?.total_jobs > 10;
  const remainingJobsCount = hasMoreJobs && item?.total_jobs - 10;
  const renderHeader = (column: ColumnConfig<Order, any>) => <th key={column.label}>{column.label}</th>;
  const renderCell = (column: ColumnConfig<Order, any>, job: OrderJob) => {
    return (
      <td style={column.width ? { minWidth: column.width, maxWidth: column.width } : {}} key={column.label}>
        {(column as any).render(job)}
      </td>
    );
  };

  let firstSetOfColumns: ColumnConfig<Order, any>[] = [
    {
      label: 'Job ID',
      width: 75,
      render: (job: OrderJob) => <strong style={{ color: 'var(--primary-main)' }}>{job.hid}</strong>
    },
    {
      label: 'Job Type',
      minWidth: 200,
      render: (job: OrderJob) => <JobTypeLabel type={job.job_type} />
    },
    {
      label: 'Title',
      width: 300,
      render: (job: OrderJob) => (
        <div>
          <Typography>
            <strong>{job.title?.name || job.description}</strong>
          </Typography>
          {job.version && (
            <Typography variant="caption" sx={{ lineHeight: 1.9, fontSize: '.9rem' }}>
              {getLineageMetaTitle(job.version, true)}
            </Typography>
          )}
        </div>
      )
    }
  ];

  let secondSetOfColumns: ColumnConfig<Order, any>[] = [
    {
      label: 'Customer',
      width: 120,
      render: (job: OrderJob) => job.customer || '-'
    },
    {
      label: 'Profile',
      width: 200,
      render: (job: OrderJob) => findandFormatUTCDateInString(job?.profile?.name)
    },
    {
      label: 'Due Date',
      width: 100,
      render: (job: OrderJob) => <DateTimeDisplay>{job.due_date as string}</DateTimeDisplay>
    },
    {
      label: 'Status',
      minWidth: 150,
      render: (job: OrderJob) => {
        return (
          <Grid container>
            <Grid item xs={12}>
              <TableStatus
                status={job.status === 'cancelled' ? job.status : job.stage || job.status}
                colorOverride={StatusColors[job.status as keyof typeof StatusColors]}
                customStatuses={job.stage ? stages : statuses}
              />
            </Grid>
            {job?.embargo_date && !job?.embargo_resumed_at && (
              <Grid item xs={12} sx={{ mt: 1 }}>
                <JobsEmbargoDate job={job} />
              </Grid>
            )}
          </Grid>
        );
      }
    },
    {
      label: 'Requirements',
      render: (job: OrderJob) => (
        <div style={{ marginLeft: 8, position: 'relative', opacity: job.status === 'cancelled' ? '0.5' : '1' }}>
          <JobRequirements sources={job.assets} />
        </div>
      )
    }
  ];

  if (extraColumns?.length) {
    extraColumns.forEach((c) => {
      firstSetOfColumns = firstSetOfColumns.filter((fc) => fc.label !== c.label);
      secondSetOfColumns = secondSetOfColumns.filter((sc) => sc.label !== c.label);
    });
  }

  const onJobClick = (e: MouseEvent) => {
    const url = `/orders/jobs/${e.currentTarget.id}`;
    // TODO: we're doing this to simulate ctrl+click,
    // but we should be using the proper element for accessibility
    if (e.ctrlKey || e.metaKey) {
      window.open(url);
    } else {
      history.push(url);
    }
  };

  // note: only available with > 10 jobs, opens jobs table with order's jobs filtered
  const handleViewMore = () => {
    dispatch(setCurrentTable('jobs'));
    dispatch(clearFilters([]));
    dispatch(applyFilter({ type: 'filter_id', selections: [item.hid, 'order_hid_filter'] }));
    history.push(`/jobs`);
  };

  const fetchJobs = async () => {
    axios
      .post(`/core/jobs/search/?page_num=0&page_size=10`, {
        order_hid_filter: [item.hid],
        order_on: 'created_at',
        direction: 'desc'
      })
      .then((response) => {
        setJobs(response.data.data);
      })
      .catch((error) => console.error(error));
  };

  useEffect(() => {
    if (!jobs && !isCollapsed) {
      fetchJobs();
    }
  }, [isCollapsed]);

  useEffect(() => {
    if (item.jobs) {
      setJobs(item.jobs);
    } else {
      if (!isCollapsed) {
        fetchJobs();
      } else {
        setJobs(undefined);
      }
    }
  }, [item]);

  const loaderRows = () => {
    const rows = [];
    const maxRows = hasMoreJobs ? 10 : item?.total_jobs || 0;
    for (let i = 0; i < maxRows; i++) {
      rows.push(
        <JobRow key={i}>
          {!isCollapsed && (
            <>
              {firstSetOfColumns.map((column) => (
                <td style={column.width ? { minWidth: column.width, maxWidth: column.width } : {}} key={column.label}>
                  <Skeleton variant="rounded" sx={{ padding: 0, margin: 0, height: '45px' }} />
                </td>
              ))}
              {extraColumns.map((column) => (
                <td style={column.width ? { minWidth: column.width, maxWidth: column.width } : {}} key={column.label}>
                  <Skeleton variant="rounded" sx={{ padding: 0, margin: 0, height: '45px' }} />
                </td>
              ))}
              {secondSetOfColumns.map((column) => (
                <td style={column.width ? { minWidth: column.width, maxWidth: column.width } : {}} key={column.label}>
                  <Skeleton variant="rounded" sx={{ padding: 0, margin: 0, height: '45px' }} />
                </td>
              ))}
            </>
          )}
        </JobRow>
      );
    }
    return rows;
  };

  const hasMoreJobsRow = (
    <HasMoreJobsRow>
      <td colSpan={2}>
        {hasMoreJobs && (
          <>
            + {remainingJobsCount} Job{remainingJobsCount === 1 ? '' : 's'}
          </>
        )}
        <Button size="small" sx={{ ml: hasMoreJobs ? 1 : -1.25 }} endIcon={<PolarisIcon icon="right" />} onClick={handleViewMore}>
          View all Jobs
        </Button>
      </td>
    </HasMoreJobsRow>
  );

  return (
    <ExpandableRow isCollapsed={isCollapsed}>
      {jobs === undefined ? (
        <JobTable>
          <thead>
            <tr>
              {firstSetOfColumns.map(renderHeader)}
              {extraColumns.map(renderHeader)}
              {secondSetOfColumns.map(renderHeader)}
            </tr>
          </thead>
          <tbody>
            {loaderRows()}
            {hasMoreJobsRow}
          </tbody>
        </JobTable>
      ) : (
        <div>
          {!sortedJobs?.length ? (
            <Typography variant="caption" sx={{ m: 4 }}>
              No jobs found.
            </Typography>
          ) : (
            <JobTable>
              <thead>
                <tr>
                  {firstSetOfColumns.map(renderHeader)}
                  {extraColumns.map(renderHeader)}
                  {secondSetOfColumns.map(renderHeader)}
                </tr>
              </thead>
              <tbody>
                {firstTenJobs.map((job) => {
                  return (
                    <JobRow key={job.id} id={job.id} onClick={onJobClick}>
                      {
                        // hiding as soon isCollapsed is false to avoid an overlapping bug during the folding animation
                        !isCollapsed && (
                          <>
                            {firstSetOfColumns.map((column) => renderCell(column, job))}
                            {extraColumns.map((column) => renderCell(column, job))}
                            {secondSetOfColumns.map((column) => renderCell(column, job))}
                          </>
                        )
                      }
                    </JobRow>
                  );
                })}
                {hasMoreJobsRow}
              </tbody>
            </JobTable>
          )}
        </div>
      )}
    </ExpandableRow>
  );
};

export default OrdersRowExpand;

const ExpandableRow = styled(TableCollapse)`
  & th,
  & td {
    border: 0;
    padding: ${({ theme }) => theme.spacing(1.5, 0.5)};
  }

  & td {
    word-break: break-word;
  }
`;

const JobTable = styled.table`
  margin-left: 18px;
  width: calc(100% - 40px);
  border-spacing: 0;
  border-collapse: collapse;

  & th {
    ${({ theme }) => convertStylesToStyled(theme?.components?.MuiTableCell?.styleOverrides?.head)}
    text-wrap: nowrap;
    opacity: 0.5;
  }

  & tr {
    background-color: initial !important;

    & th:first-of-type,
    & td:first-of-type {
      padding-left: 15px;
    }
  }
`;

const JobRow = styled.tr`
  text-decoration: none;
  color: ${({ theme }) => theme.palette.text.primary};
  transition: ease background-color 0.2s;

  & > td:first-of-type {
    border-top-left-radius: ${({ theme }) => theme.shape.borderRadius};
    border-bottom-left-radius: ${({ theme }) => theme.shape.borderRadius};
  }

  & > td:last-of-type {
    border-top-right-radius: ${({ theme }) => theme.shape.borderRadius};
    border-bottom-right-radius: ${({ theme }) => theme.shape.borderRadius};
  }

  &:hover {
    cursor: pointer;
    transition: ease background-color 0.2s;
  }

  & .MuiTypography-subtitle1 {
    color: ${({ theme }) => theme.palette.secondary.light};
  }
`;

const HasMoreJobsRow = styled.tr`
  &:hover {
    background-color: initial !important;
  }
`;
